import '../broiler/poultryDesign.css';
import Modal, {ModalFooter} from '../shared/Modal.js';
import Segmented from '../shared/Segmented.js';
import Button from '../shared/Button.js';
import React from 'react';
import {useQueryClient} from '@tanstack/react-query';
import {useAppLocation as useLocation, useAppNavigate as useNavigate} from '../app/navigation/index.js';
import {openableProps} from '../shared/openable.js';
import {getProgramColor} from '../lib/programColors.js';

import RecordCollaborationSection from '../shared/RecordCollaborationSection.tsx';
import RetirementHomeHistory from './RetirementHomeHistory.js';

import RecordSequenceNav from '../shared/RecordSequenceNav.tsx';
import {recordSeqNavOptions, labeledSeqItems} from '../lib/recordSequence.js';

import InlineNotice from '../shared/InlineNotice.jsx';
import {useConfirmedDraftAutosave} from '../shared/useConfirmedDraftAutosave.js';
import {layerAllocationConflicts, layerAllocationKey} from './layerAllocationConflicts.js';
import RecordPageLoadError from '../shared/RecordPageLoadError.jsx';

import {
  RecordPageFrame,
  RecordPageLoading,
  RecordPageNotFound,
  RecordPageBody,
  RecordBackLink,
  RecordTitle,
} from '../shared/RecordPageShell.jsx';

import {S, getReadableText} from '../lib/styles.js';

import Badge from '../shared/Badge.jsx';
import {GroupDot} from '../shared/GroupPillPicker.js';
import {buildLayerGroupPalette, layerGroupColor, layerGroupTint} from './layerGroupPalette.js';

import StatusText from '../shared/StatusText.jsx';
import {recordFieldRowClass, recordFieldLabel, recordControl, recordTextarea} from '../shared/recordPageControls.jsx';
import {toISO, addDays} from '../lib/dateUtils.js';
import {BROODERS, SCHOONERS} from '../lib/broiler.js';
import {computeProjectedCount, computeHousingDisplayCount, computeLayerFeedCost} from '../lib/layerHousing.js';
import {getHousingCap, computeBatchStats, computeHousingStats} from './layerBatchStats.js';
import {deleteLayerBatch} from '../lib/layerBatchDeleteApi.js';
import {layerPatch, layerRejectionMessage, type LayerOpOutcome} from '../lib/layerOperationsApi.js';
import {
  LAYER_BATCH_FIELDS,
  LAYER_FIELD_LABELS,
  RETIREMENT_HOME_HOUSING_FIELDS,
  confirmedRow,
  createLayerOpRunner,
} from './layerRecordOps.js';
import PigConflictNotice, {type PigConflictState} from '../pig/PigConflictNotice.js';
import {
  LAYER_GROUPS_CONFLICT_MESSAGE,
  reportAppStoreSaveStatus,
  saveLayerGroupsDocument,
} from '../lib/appStoreOperationApi.js';
import {
  buildLayerGroupStatusIndex,
  layerGroupStatusLabel,
  LAYER_GROUP_STATUS_OPTIONS,
  normalizeLayerGroupName,
} from '../lib/layerGroupStatus.js';
import type {LayerGroupStatus} from '../lib/layerGroupStatus.js';
import {useAuth} from '../contexts/AuthContext.js';
import {useLayer} from '../contexts/LayerContext.js';
import {useLayerGroupBlobEviction} from '../app/data/layerGroupBlobCache.js';
import {useConfirm} from '../contexts/ConfirmContext.jsx';
import {RESOURCE} from '../app/data/routeDataCatalog.js';
import type {LayerRecordAppStoreData, LayerRecordCoreData} from '../app/data/routeDataCatalog.js';
import {useRouteResource} from '../app/data/useRouteResource.js';
import Header from '../shared/Header.tsx';
import type {Dispatch, SetStateAction} from 'react';
import type {AuthState} from '../contexts/AuthContext.js';
import type {Notice} from '../shared/InlineNotice.js';
import type {SupabaseClient} from '../lib/supabaseTypes.js';
import type {ISODateInput} from '../lib/dateUtils.js';
import type {RecordSeqEntry, RecordSeqId} from '../lib/recordSequence.js';
import type {EggDailyRow} from './layerBatchStats.js';

// ---------------------------------------------------------------------------
// Row + draft shapes this page reads and writes.
// ---------------------------------------------------------------------------

// A layer_batches row as THIS page reads and rewrites it. Deliberately not
// layerBatchStats' LayerBatchRow: that type describes the three columns the
// STATS module needs, and this page owns the whole record. Every field is
// nullable because buildBatchRec below writes null for an empty form field, and
// the numeric ones are `number | null` because that same writer runs each one
// through parseInt/parseFloat before it reaches the table.
type BatchRow = {
  id: string;
  name?: string | null;
  status?: string | null;
  arrival_date?: string | null;
  original_count?: number | null;
  supplier?: string | null;
  cost_per_bird?: number | null;
  brooder_name?: string | null;
  brooder_entry_date?: string | null;
  brooder_exit_date?: string | null;
  schooner_name?: string | null;
  schooner_entry_date?: string | null;
  schooner_exit_date?: string | null;
  notes?: string | null;
  per_lb_starter_cost?: number | null;
  per_lb_grower_cost?: number | null;
  per_lb_layer_cost?: number | null;
};

// A layer_housings row, reduced to the columns this page reads.
type HousingRow = {
  id: string;
  batch_id?: string | null;
  housing_name?: string | null;
  status?: string | null;
  current_count?: number | null;
  current_count_date?: string | null;
  start_date?: string | null;
  retired_date?: string | null;
  notes?: string | null;
};

// A broiler ppp-v4 batch as the brooder/schooner conflict probes read it. The
// two `brooder_*` spellings are the fallbacks the probe itself writes: a broiler
// row uses brooderIn/brooderOut, and the `|| bt.brooder_exit_date` arm exists for
// rows that were imported with the layer column names.
type ConflictBatchRow = {
  id?: string;
  brooder?: string | number | null;
  schooner?: string | number | null;
  brooderIn?: string | null;
  brooderOut?: string | null;
  schoonerIn?: string | null;
  schoonerOut?: string | null;
  brooder_entry_date?: string | null;
  brooder_exit_date?: string | null;
  schooner_entry_date?: string | null;
  schooner_exit_date?: string | null;
};

// A legacy layer group, reduced to the two fields the housing picker reads.
// Not `| null`: LayersView, the only writer of these rows, builds every one of
// them from a form whose name field is a string.
type GroupRow = {id?: string | number; name?: string; status?: string; [field: string]: unknown};

// A layer_dailys row as THIS page reads it. Wider than layerBatchStats'
// LayerDailyRow because the Retirement-Home window below also reads batch_id and
// re-derives the feed phase itself.
type PageLayerDailyRow = {
  batch_id?: string | null;
  batch_label?: string | null;
  date?: string | null;
  feed_lbs?: string | number | null;
  mortality_count?: string | number | null;
  layer_count?: string | number | null;
  feed_type?: string | null;
};

// The batch edit draft. Every field is a string because it is bound straight to
// a form control; buildBatchRec is where the numeric ones become numbers.
type BatchDraft = {
  name: string;
  status: string;
  arrival_date: string;
  original_count: string;
  supplier: string;
  cost_per_bird: string;
  brooder_name: string;
  brooder_entry_date: string;
  brooder_exit_date: string;
  schooner_name: string;
  schooner_entry_date: string;
  schooner_exit_date: string;
  notes: string;
  current_count: string;
  start_date: string;
  per_lb_starter_cost: string;
  per_lb_grower_cost: string;
  per_lb_layer_cost: string;
};

const EMPTY_BATCH_DRAFT: BatchDraft = {
  name: '',
  status: 'active',
  arrival_date: '',
  original_count: '',
  supplier: '',
  cost_per_bird: '',
  brooder_name: '',
  brooder_entry_date: '',
  brooder_exit_date: '',
  schooner_name: '',
  schooner_entry_date: '',
  schooner_exit_date: '',
  notes: '',
  current_count: '',
  start_date: '',
  per_lb_starter_cost: '',
  per_lb_grower_cost: '',
  per_lb_layer_cost: '',
};

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// LayerBatchesView builds this object literal explicitly at BOTH of this page's
// mount sites — LayerBatchesRouter's `/layer/batches/` branch for the shapes the
// boot release did not audit, and LayerBatchRecordMount for the released record
// route — and they are asserted character-identical in
// tests/static/layer_batches_detail_boot_release_static.test.js. Every prop
// below is one they really pass, and after the boot release there are three.
//
// SEVEN PROPS LEFT AND DID NOT COME BACK. layerGroups, layerBatches,
// layerHousings and batches were App's former-eager boot results, threaded here
// by LayerEntry through the router; this page reads all four for itself now,
// through layer.record.core and layer.record.appStore, which is what lets
// `layerbatches` retire from the frozen consumer lists in
// tests/fixtures/former-eager-dependencies.json. `feedCosts` left for a
// different reason: it was declared, destructured, and never read.
//
// setLayerBatches AND setLayerHousings LEFT WITH THE BOOT RELEASE, and neither
// was ever the database write. Since migration 242 (R4) this page's writes are
// layer_apply_operation calls through src/layer/layerRecordOps.ts: batch.update
// in `persistBatchRec`, retirement_home.update in
// `persistRetirementHomeDetails`, housing.create in `saveNewHousing` — one
// audited operation each, checked against the persisted row, with the Activity
// written by the server in the same transaction. `deleteLayerBatch(sb, …)` — a
// SECDEF RPC — in `handleDeleteBatch` is untouched. The page still decides WHEN
// each one runs and owns every error branch and overlay repaint around it.
//
// What left with the release is App's POST-write fan-out: LayerContext's raw
// `setLayerBatches`, which performs no write of its own, and
// `persistLayerHousings`, which was only `setLayerHousings(nb)` plus maintenance
// notification and had no `sbSave` either. Both are redundant on a released
// surface: this page owns and revalidates its record resources, and no route
// transition repopulates a global LayerContext directory. See this surface's
// declaration for why removing them had to land with the release itself.
type LayerBatchPageProps = {
  sb: SupabaseClient;
  fmt: (iso: ISODateInput) => string;
  authState: AuthState;
};

const LAYER_RECORD_PARAMS = Object.freeze({});
const EMPTY_LAYER_RECORD_ROWS: unknown[] = [];

// The record's editable cards (R25). Retirement Home shows only details/notes.
type BatchEditorCard = 'details' | 'lifecycle' | 'notes';

// R08: a record card is radius 18 on --shadow-card. Card and section titles are
// the 16/800 and 18/800 headings the cattle and sheep records use.
const recordCardS: React.CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--r-card)',
  boxShadow: 'var(--shadow-card)',
};
const cardTitleS: React.CSSProperties = {fontSize: 16, fontWeight: 800, color: 'var(--ink)'};
// R25: a card's one text action (Edit), brand ink, no button chrome.
const cardEditActionS: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: '6px 2px',
  minHeight: 32,
  color: 'var(--brand)',
  fontFamily: 'inherit',
  fontSize: 13.5,
  fontWeight: 700,
  cursor: 'pointer',
};
const sectionTitleS: React.CSSProperties = {
  margin: 0,
  fontSize: 18,
  fontWeight: 800,
  letterSpacing: '-.01em',
  color: 'var(--ink)',
};

function retirementHomeNotes(batchNotes?: string | null, housingNotes?: string | null): string {
  const primary = batchNotes || '';
  const secondary = housingNotes || '';
  if (!secondary || primary.includes(secondary)) return primary;
  if (!primary || secondary.includes(primary)) return secondary;
  return primary + '\n\n' + secondary;
}

export default function LayerBatchPage({sb, fmt, authState}: LayerBatchPageProps) {
  const queryClient = useQueryClient();
  const {setSaveStatus} = useAuth();
  // 242: every write on this page is ONE layer_apply_operation (src/layer/
  // layerRecordOps.ts); the server writes its Activity in the same transaction.
  const [applyLayerOp] = React.useState(() => createLayerOpRunner({sb, onSaveStatus: setSaveStatus, queryClient}));
  const [batchConflict, setBatchConflict] = React.useState<PigConflictState | null>(null);
  const {setLayerGroups} = useLayer();
  const evictLayerGroupBlobReads = useLayerGroupBlobEviction();
  const navigate = useNavigate();
  // `location.state` is `unknown` by design (src/app/navigation/appLocation.ts): the
  // write side normalized it and no consumer may read it without proving its
  // shape. The route state this page reads is exactly the one
  // recordSeqNavOptions writes, so that proof is applied ONCE, here at the hook
  // call, instead of at the read — which leaves the read the same expression it
  // has always been. The location shape is taken from the facade hook itself, so
  // no facade internal is imported.
  const location = useLocation() as Omit<ReturnType<typeof useLocation>, 'state'> & {
    state?: {recordSeq?: RecordSeqEntry[]} | null;
  };
  const {confirmDelete} = useConfirm();
  const batchId = location.pathname.slice('/layer/batches/'.length);
  // Originating list order handed through route state; absent on direct links
  // and notification/deep-link opens, so the controls hide.
  const recordSeq = location.state?.recordSeq || null;
  function navigateSeq(id: RecordSeqId) {
    navigate('/layer/batches/' + id, recordSeqNavOptions(recordSeq));
  }

  // ── The reads this page owns ─────────────────────────────────────────────
  // Two resources, because they have different shapes and different failure
  // costs: layer.record.core is the four row feeds, layer.record.appStore is
  // the two persisted blobs. Both used to arrive as LayerEntry props off App's
  // boot; the metric halves of the first used to be paginated here by a
  // dynamic-table helper — the table arrived as a PARAMETER, so both of its
  // reads inventoried as `from:~table` and could prove ownership of neither
  // literal table.
  const core = useRouteResource(RESOURCE.LAYER_RECORD_CORE, LAYER_RECORD_PARAMS);
  const appStore = useRouteResource(RESOURCE.LAYER_RECORD_APP_STORE, LAYER_RECORD_PARAMS);
  const coreData = core.data as LayerRecordCoreData | null;
  const appStoreData = appStore.data as LayerRecordAppStoreData | null;

  // THE OVERLAYS EXIST BECAUSE A QUERY CANNOT REPAINT SYNCHRONOUSLY.
  //
  // This page used to re-render from the App context array its setter had just
  // updated, so a save, a delete or a new housing was on screen immediately.
  // The page now renders from the resource, so each mutation is projected
  // locally instead. Same values, same call signatures, same instant paint.
  //
  // Since the boot release the overlays are the WHOLE of the local publish: the
  // App fan-out each wrapper used to call afterwards is gone, so these are plain
  // state setters with names that still say what they mean at their call sites.
  const [batchOverlay, setBatchOverlay] = React.useState<unknown[] | null>(null);
  const [housingOverlay, setHousingOverlay] = React.useState<unknown[] | null>(null);
  const [groupOverlay, setGroupOverlay] = React.useState<GroupRow[] | null>(null);
  const serverBatches = coreData?.layerBatches || EMPTY_LAYER_RECORD_ROWS;
  const serverHousings = coreData?.layerHousings || EMPTY_LAYER_RECORD_ROWS;
  const setLayerBatches = React.useCallback<Dispatch<SetStateAction<unknown[]>>>(
    (action) => {
      setBatchOverlay((prev) =>
        typeof action === 'function'
          ? (action as (previous: unknown[]) => unknown[])(prev ?? serverBatches)
          : (action as unknown[]),
      );
    },
    [serverBatches],
  );
  const setLayerHousings = React.useCallback((next: HousingRow[]) => {
    setHousingOverlay(next);
  }, []);

  // The one place this page states what it believes the four opaque arrays
  // hold; every field read below is then checked against these types.
  const layerGroups = (groupOverlay ?? appStoreData?.layerGroups ?? EMPTY_LAYER_RECORD_ROWS) as GroupRow[];
  const retirementHomeStatus = buildLayerGroupStatusIndex(layerGroups).get('retirement home');
  // R06: group colors by canonical order. A batch is not a group and takes no
  // dot; its housings are, and so is the Retirement Home record.
  const groupPalette = buildLayerGroupPalette(layerGroups);
  const layerBatches = (batchOverlay ?? serverBatches) as BatchRow[];
  const layerHousings = (housingOverlay ?? serverHousings) as HousingRow[];
  const conflictBatches = (appStoreData?.broilerBatches || EMPTY_LAYER_RECORD_ROWS) as ConflictBatchRow[];

  const role = authState && authState.role;
  const canEdit = role === 'admin' || role === 'management';

  // Resolve the batch from the directory this page loaded. It used to come from
  // App's boot arrays, which main.jsx guaranteed were populated before any layer
  // route rendered; the guarantee is now this page's own `loading` gate, checked
  // below before the value is read.
  const batch = React.useMemo(
    () => (layerBatches || []).find((b) => b.id === batchId) || null,
    [layerBatches, batchId],
  );
  const retirementHousings = layerHousings.filter((h) => h.batch_id === batchId);
  const retirementHousing =
    retirementHousings.find(
      (h) => h.status === 'active' && normalizeLayerGroupName(h.housing_name) === 'retirement home',
    ) ||
    retirementHousings.find((h) => normalizeLayerGroupName(h.housing_name) === 'retirement home') ||
    retirementHousings[0] ||
    null;
  const rawLayerDailys = (coreData?.layerDailys || EMPTY_LAYER_RECORD_ROWS) as PageLayerDailyRow[];
  const rawEggDailys = (coreData?.eggDailys || EMPTY_LAYER_RECORD_ROWS) as EggDailyRow[];
  const loading = core.loading || appStore.loading;
  const resourceError = core.error || appStore.error;
  const loadError: Notice | null = resourceError
    ? {
        kind: 'error',
        message:
          'Could not load layer batch metrics: ' +
          ((resourceError as {message?: string}).message || String(resourceError)),
      }
    : null;
  const [notice, setNotice] = React.useState<Notice | null>(null);
  const [retHomePeriod, setRetHomePeriod] = React.useState(30);

  // Batch editor state. R25 (owner amendment 2026-10-24: autosave with Done):
  // the record reads first, and one card at a time flips to inputs in place.
  // Every card edits the SAME draft (bForm) through the same confirmed
  // autosave, so the card split is presentation only.
  const [editingCard, setEditingCard] = React.useState<BatchEditorCard | null>(null);
  const [bForm, setBForm] = React.useState<BatchDraft>(EMPTY_BATCH_DRAFT);
  const [groupSaving, setGroupSaving] = React.useState(false);
  const [groupError, setGroupError] = React.useState('');
  const [err, setErr] = React.useState('');
  const bFormRef = React.useRef(bForm);
  const confirmedAllocation = React.useRef<string | null>(null);
  const [allocationWarning, setAllocationWarning] = React.useState<string[]>([]);
  const deletingBatch = React.useRef(false);
  const closingBatch = React.useRef(false);
  const [batchActionBusy, setBatchActionBusy] = React.useState(false);
  const {controller: batchDraft, state: batchSaveState} = useConfirmedDraftAutosave<BatchDraft>({
    ownerKey: 'layer.batch.editor',
    debounceMs: 1500,
    isBusy: () => deletingBatch.current || closingBatch.current || groupSaving,
    validate: (draft) => {
      if (!draft.name.trim()) {
        setErr('Batch name is required.');
        return false;
      }
      const key = layerAllocationKey(draft);
      const warnings =
        batch?.name === 'Retirement Home' || key === layerAllocationKey(batch || {})
          ? []
          : layerAllocationConflicts(draft, batch?.id || '', conflictBatches, layerBatches);
      setAllocationWarning(warnings);
      if (warnings.length && confirmedAllocation.current !== key) return false;
      setErr('');
      return true;
    },
    persist: (draft) =>
      batch?.name === 'Retirement Home' ? persistRetirementHomeDetails(draft) : persistBatchRec(buildBatchRec(draft)),
  });
  const batchSaving = batchSaveState === 'saving' || batchActionBusy;
  const batchPending = batchSaveState !== 'idle';
  React.useEffect(() => {
    if (!batchDraft.isDirty()) setEditingCard(null);
  }, [batchId, batchDraft]);
  const retirementHousingDraftRef = React.useRef<HousingRow | null>(null);
  // 242: the last row the SERVER confirmed, set synchronously from each reply
  // (as LayerHousingPage's savedHousing). The autosave drain persists a queued
  // revision before React re-renders, so render state (`batch`,
  // `layerHousings`) can still be the pre-save row; expected-before values come
  // from these refs whenever they hold the record being edited (REVIEW-242 F1).
  const savedBatchRef = React.useRef<BatchRow | null>(null);
  const savedRhHousingRef = React.useRef<HousingRow | null>(null);
  React.useEffect(() => {
    savedBatchRef.current = null;
    savedRhHousingRef.current = null;
  }, [batchId]);
  // One housing id per Add Housing intent (name + start date), so a retry after
  // an unknown reply is the same request and replays (REVIEW-242 F3).
  const addHousingIdRef = React.useRef<{key: string; id: string} | null>(null);

  // + Add Housing modal state (tiny helper — just picker + start_date, then nav)
  const [showAddHousing, setShowAddHousing] = React.useState(false);
  const [addHousingName, setAddHousingName] = React.useState('');
  const [addHousingStart, setAddHousingStart] = React.useState(todayStr());
  const [addHousingErr, setAddHousingErr] = React.useState('');
  const [addHousingBusy, setAddHousingBusy] = React.useState(false);

  // The paginated metric loader that used to live here belongs to
  // layer.record.core now, read above. Its two dynamic-table calls are retired
  // in tests/fixtures/render-read-sites.json: with the table as a parameter
  // they inventoried as `from:~table` and could prove ownership of neither
  // literal table.
  // A Retry is a fresh server answer, so the local overlays go with it: a
  // failed-then-retried page must not keep projecting a mutation over rows that
  // already contain it.
  const loadAll = React.useCallback(async () => {
    setBatchOverlay(null);
    setHousingOverlay(null);
    setGroupOverlay(null);
    const [fresh] = (await Promise.all([core.refetch(), appStore.refetch()])) as [
      {data?: LayerRecordCoreData} | undefined,
      unknown,
    ];
    // The confirmed rows follow the server's answer, not the next render.
    if (fresh?.data) {
      const rows = (fresh.data.layerBatches || []) as BatchRow[];
      const housings = (fresh.data.layerHousings || []) as HousingRow[];
      savedBatchRef.current = rows.find((b) => b.id === batchId) || null;
      const rh = savedRhHousingRef.current;
      savedRhHousingRef.current = rh ? housings.find((h) => h.id === rh.id) || null : null;
    } else {
      savedBatchRef.current = null;
      savedRhHousingRef.current = null;
    }
  }, [appStore, core, batchId]);

  async function saveRetirementHomeStatus(status: LayerGroupStatus) {
    if (!canEdit || batch?.name !== 'Retirement Home' || groupSaving) return;
    setGroupSaving(true);
    setGroupError('');
    setSaveStatus('saving');
    try {
      const createdId = crypto.randomUUID();
      for (let attempt = 0; attempt < 2; attempt += 1) {
        // Read the current document before changing one field, preserving other
        // groups and any fields added since this record page was opened.
        const refreshed = (await appStore.refetch()) as {data?: LayerRecordAppStoreData; error?: unknown} | undefined;
        if (refreshed?.error || !Array.isArray(refreshed?.data?.layerGroups)) {
          reportAppStoreSaveStatus(setSaveStatus, 'error');
          throw new Error('Could not load the current layer groups. Try again.');
        }
        let found = false;
        const next = refreshed.data.layerGroups.map((row) => {
          if (!row || typeof row !== 'object') return row;
          const group = row as GroupRow;
          if (normalizeLayerGroupName(group.name) !== 'retirement home') return row;
          found = true;
          return {...group, status};
        });
        if (!found) next.push({id: createdId, name: 'Retirement Home', status});
        const outcome = await saveLayerGroupsDocument(sb, {
          expectedUpdatedAt: refreshed.data.layerGroupsUpdatedAt ?? null,
          document: next,
        });
        if (outcome.status === 'saved' || outcome.status === 'no_change') {
          evictLayerGroupBlobReads();
          setLayerGroups(next);
          setGroupOverlay(next as GroupRow[]);
          reportAppStoreSaveStatus(setSaveStatus, 'saved');
          return;
        }
        if (outcome.status === 'conflict' && attempt === 0) {
          continue;
        }
        reportAppStoreSaveStatus(setSaveStatus, 'error');
        if (outcome.status === 'conflict') {
          throw new Error(LAYER_GROUPS_CONFLICT_MESSAGE);
        }
        throw new Error('Status could not be saved. Try again.');
      }
    } catch (error) {
      setGroupError(error instanceof Error ? error.message : 'Status could not be saved. Try again.');
    } finally {
      setGroupSaving(false);
    }
  }

  React.useEffect(() => {
    setNotice(null);
  }, [batchId, authState]);

  React.useEffect(() => {
    if (!loading && location.hash) {
      const anchor = location.hash.slice(1);
      setTimeout(() => {
        const el = document.getElementById(anchor);
        if (el) el.scrollIntoView({behavior: 'smooth', block: 'center'});
      }, 200);
    }
  }, [loading, location.hash]);

  function buildBatchRec(formSnapshot: BatchDraft): BatchRow {
    const f = formSnapshot;
    return {
      id: batch ? batch.id : f.name.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
      name: f.name.trim(),
      status: f.status || 'active',
      arrival_date: f.arrival_date || null,
      original_count: f.original_count !== '' ? parseInt(f.original_count) : null,
      supplier: f.supplier || null,
      // Cents, as PROD's numeric(8,2) column stores it. A 3-decimal value would
      // be rounded by the column while the draft kept it, so every later save
      // would resend it as a change and log it again (242 v2 PROD-shape proof).
      cost_per_bird: f.cost_per_bird !== '' ? Math.round(parseFloat(f.cost_per_bird) * 100) / 100 : null,
      brooder_name: f.brooder_name || null,
      brooder_entry_date: f.brooder_entry_date || null,
      brooder_exit_date: f.brooder_exit_date || null,
      schooner_name: f.schooner_name || null,
      schooner_entry_date: f.schooner_entry_date || null,
      schooner_exit_date: f.schooner_exit_date || null,
      notes: f.notes || null,
      per_lb_starter_cost: f.per_lb_starter_cost !== '' ? parseFloat(f.per_lb_starter_cost) : null,
      per_lb_grower_cost: f.per_lb_grower_cost !== '' ? parseFloat(f.per_lb_grower_cost) : null,
      per_lb_layer_cost: f.per_lb_layer_cost !== '' ? parseFloat(f.per_lb_layer_cost) : null,
    };
  }

  // 242: ONE batch.update of only the fields that changed, checked against the
  // persisted row (expected-before). The server logs status.changed / field.updated
  // in the same transaction; the page no longer writes Activity.
  async function persistBatchRec(rec: BatchRow) {
    if (!batch) return false;
    const {fields, expected, change} = layerPatch(confirmedBatch(), rec, LAYER_BATCH_FIELDS);
    if (!fields.length) return true;
    const outcome = await applyLayerOp({operation: 'batch.update', target: {batch_id: batch.id}, expected, change});
    return settleBatchOutcome(outcome);
  }

  // The persisted batch row: the server's last confirmed reply when it is this
  // batch, else the rendered row.
  function confirmedBatch(): BatchRow | null {
    return batch ? confirmedRow(savedBatchRef.current, batch.id, batch) : null;
  }

  // One reading of every outcome for this editor. A conflict keeps the draft and
  // offers "Use latest" (coordinator ruling Q2 for 231: no "keep mine").
  function settleBatchOutcome(outcome: LayerOpOutcome): boolean {
    if (outcome.status === 'saved' || outcome.status === 'no_change') {
      const saved = outcome.rows?.batch as BatchRow | undefined;
      if (saved) {
        savedBatchRef.current = saved;
        setLayerBatches((prevRaw) => (prevRaw as BatchRow[]).map((b) => (b.id === saved.id ? saved : b)));
      }
      if (outcome.status === 'saved' && outcome.mustReread) void loadAll();
      setBatchConflict(null);
      setErr('');
      return true;
    }
    if (outcome.status === 'conflict') {
      const conflicts = outcome.conflicts;
      void loadAll();
      setBatchConflict({
        conflicts,
        labels: LAYER_FIELD_LABELS,
        useLatest: async () => {
          await loadAll();
          setBatchConflict(null);
          const merged = {...bFormRef.current} as Record<string, string>;
          for (const c of conflicts)
            if (c.field in merged) merged[c.field] = c.current == null ? '' : String(c.current);
          const next = merged as BatchDraft;
          bFormRef.current = next;
          setBForm(next);
          batchDraft.update(next);
        },
      });
      return false;
    }
    if (outcome.status === 'unknown') void loadAll();
    setErr(
      outcome.status === 'rejected'
        ? 'Not saved: ' + layerRejectionMessage(outcome)
        : outcome.status === 'refused'
          ? 'Not saved: ' + outcome.reason
          : outcome.message,
    );
    return false;
  }

  // 242: the combined editor's housing and batch halves in ONE transaction
  // (retirement_home.update). The server stamps the count date with the farm
  // date, applies the hen-count anchor rule, and creates the Retirement Home
  // housing on the first save.
  async function persistRetirementHomeDetails(formSnapshot: BatchDraft) {
    const housing = retirementHousingDraftRef.current;
    if (!canEdit || !batch || batch.name !== 'Retirement Home' || !housing) return false;
    const count = formSnapshot.current_count === '' ? null : Number(formSnapshot.current_count);
    if (count !== null && (!Number.isInteger(count) || count < 0)) {
      setErr('Current count must be a whole number of hens, zero or greater.');
      return false;
    }
    const stored = confirmedRow(
      savedRhHousingRef.current,
      housing.id,
      layerHousings.find((h) => h.id === housing.id),
    );
    const housingNext = {
      current_count: count,
      start_date: formSnapshot.start_date || null,
      notes: formSnapshot.notes || null,
    };
    const hp = stored
      ? layerPatch(stored, housingNext, RETIREMENT_HOME_HOUSING_FIELDS)
      : {fields: Object.keys(housingNext), expected: {}, change: housingNext};
    const bp = layerPatch(
      confirmedBatch(),
      buildBatchRec(formSnapshot),
      LAYER_BATCH_FIELDS.filter((field) => field !== 'name'),
    );
    if (stored && !hp.fields.length && !bp.fields.length) return true;
    setErr('');
    const outcome = await applyLayerOp({
      operation: 'retirement_home.update',
      target: {batch_id: batch.id, housing_id: housing.id},
      expected: {batch: bp.expected, housing: hp.expected},
      change: {batch: bp.change, housing: hp.change},
    });
    if (outcome.status === 'saved' || outcome.status === 'no_change') {
      const savedHousing = outcome.rows?.housing as HousingRow | undefined;
      if (savedHousing) {
        retirementHousingDraftRef.current = savedHousing;
        savedRhHousingRef.current = savedHousing;
        setHousingOverlay((previous) => {
          const rows = (previous ?? serverHousings) as HousingRow[];
          return rows.some((row) => row.id === savedHousing.id)
            ? rows.map((row) => (row.id === savedHousing.id ? savedHousing : row))
            : [...rows, savedHousing];
        });
      }
    }
    return settleBatchOutcome(outcome);
  }

  function scheduleBatchAutosave(formSnapshot: BatchDraft) {
    batchDraft.update(formSnapshot);
  }

  async function flushBatchAutosave() {
    return batchDraft.flush();
  }

  function updBatch(updater: Partial<BatchDraft> | ((prev: BatchDraft) => BatchDraft)) {
    if (deletingBatch.current || closingBatch.current) return;
    const next: BatchDraft =
      typeof updater === 'function' ? updater(bFormRef.current) : {...bFormRef.current, ...updater};
    bFormRef.current = next;
    setBForm(next);
    setAllocationWarning([]);
    scheduleBatchAutosave(next);
  }

  function openEdit(card: BatchEditorCard) {
    if (!canEdit || !batch) return;
    if (batchDraft.isDirty()) {
      setEditingCard(card);
      return;
    }
    confirmedAllocation.current = null;
    setAllocationWarning([]);
    const isRetirementHome = batch.name === 'Retirement Home';
    retirementHousingDraftRef.current = isRetirementHome
      ? retirementHousing || {
          id: crypto.randomUUID(),
          batch_id: batch.id,
          housing_name: 'Retirement Home',
          status: 'active',
          current_count: null,
          current_count_date: null,
          start_date: null,
          retired_date: null,
          notes: null,
        }
      : null;
    // Every field is normalised to a string because the DRAFT is all strings (it
    // binds to form controls) while the stored row holds numbers and nulls. Each
    // of these already reached an <input value=...> as a number or an empty
    // string and React stringifies a value attribute either way, so the rendered
    // control is unchanged; what changes is that the draft now says so.
    const draft: BatchDraft = {
      ...EMPTY_BATCH_DRAFT,
      ...batch,
      name: batch.name || '',
      status: batch.status || 'active',
      arrival_date: batch.arrival_date || '',
      supplier: batch.supplier || '',
      brooder_name: batch.brooder_name || '',
      brooder_entry_date: batch.brooder_entry_date || '',
      brooder_exit_date: batch.brooder_exit_date || '',
      schooner_name: batch.schooner_name || '',
      schooner_entry_date: batch.schooner_entry_date || '',
      schooner_exit_date: batch.schooner_exit_date || '',
      original_count: batch.original_count != null ? String(batch.original_count) : '',
      cost_per_bird: batch.cost_per_bird != null ? String(batch.cost_per_bird) : '',
      per_lb_starter_cost: batch.per_lb_starter_cost != null ? String(batch.per_lb_starter_cost) : '',
      per_lb_grower_cost: batch.per_lb_grower_cost != null ? String(batch.per_lb_grower_cost) : '',
      per_lb_layer_cost: batch.per_lb_layer_cost != null ? String(batch.per_lb_layer_cost) : '',
      notes: isRetirementHome ? retirementHomeNotes(batch.notes, retirementHousing?.notes) : batch.notes || '',
      current_count:
        isRetirementHome && retirementHousing?.current_count != null ? String(retirementHousing.current_count) : '',
      start_date: isRetirementHome ? retirementHousing?.start_date || '' : '',
    };
    bFormRef.current = draft;
    setBForm(draft);
    setErr('');
    setEditingCard(card);
  }

  // A card's Edit. With no editor open it opens one exactly as the modal did.
  // With another card open, that card settles FIRST through the same flush
  // Done uses: on success the shared draft carries over to the new card; on a
  // failed save the open editor stays on screen with its draft and notices, so
  // nothing typed into another card is ever discarded.
  async function editCard(card: BatchEditorCard) {
    if (!canEdit || !batch || editingCard === card) return;
    if (groupSaving || deletingBatch.current || closingBatch.current) return;
    if (editingCard === null) {
      openEdit(card);
      return;
    }
    closingBatch.current = true;
    setBatchActionBusy(true);
    try {
      if (!(await flushBatchAutosave())) return;
      setErr('');
      setEditingCard(card);
    } finally {
      closingBatch.current = false;
      setBatchActionBusy(false);
    }
  }

  async function closeBatchForm() {
    if (groupSaving || deletingBatch.current || closingBatch.current) return;
    closingBatch.current = true;
    setBatchActionBusy(true);
    try {
      const ok = await flushBatchAutosave();
      if (!ok) return;
      // The notes change was logged by the server with the save itself (242).
      setEditingCard(null);
      bFormRef.current = EMPTY_BATCH_DRAFT;
      setBForm(EMPTY_BATCH_DRAFT);
      setErr('');
    } finally {
      closingBatch.current = false;
      setBatchActionBusy(false);
    }
  }

  function handleDeleteBatch() {
    // No `typeof confirmDelete !== 'function'` bail-out: that check silently
    // disabled the delete action whenever the prop was missing. The opener now
    // comes from ConfirmContext and is always present.
    if (!canEdit || !batch) return;
    confirmDelete(
      'Delete batch ' + batch.name + '? This will also delete all its housings. This cannot be undone.',
      async () => {
        if (deletingBatch.current || closingBatch.current) return;
        deletingBatch.current = true;
        setBatchActionBusy(true);
        try {
          if (!(await flushBatchAutosave())) return;
          // Transactional delete via SECDEF RPC (mig 106): clears child housings +
          // deletes the batch root + writes one deletion audit event server-side,
          // all in one transaction. Replaces the two raw client deletes.
          const res = await deleteLayerBatch(sb, batch.id);
          if (!res || !res.ok) {
            setNotice({kind: 'error', message: 'Delete failed: ' + (res?.error || res?.reason || 'unknown error')});
            return;
          }
          if (typeof setLayerHousings === 'function') {
            const nextHousings = (layerHousings || []).filter((h) => h.batch_id !== batch.id);
            setLayerHousings(nextHousings);
          }
          if (typeof setLayerBatches === 'function') {
            setLayerBatches((prev) => (prev as BatchRow[]).filter((b) => b.id !== batch.id));
          }
          deletingBatch.current = false;
          navigate('/layer/batches');
        } catch (error) {
          setNotice({
            kind: 'error',
            message: 'Delete failed: ' + (error instanceof Error ? error.message : String(error)),
          });
        } finally {
          deletingBatch.current = false;
          setBatchActionBusy(false);
        }
      },
    );
  }

  async function saveNewHousing() {
    if (!canEdit || !batch) return;
    setAddHousingErr('');
    const name = (addHousingName || '').trim();
    if (!name) {
      setAddHousingErr('Pick a housing first.');
      return;
    }
    setAddHousingBusy(true);
    // The same intent (housing + start date) keeps its id until it is saved, so a
    // retry after an unknown reply replays instead of adding a second housing.
    const intentKey = name + '|' + (addHousingStart || '');
    if (!addHousingIdRef.current || addHousingIdRef.current.key !== intentKey)
      addHousingIdRef.current = {key: intentKey, id: String(Date.now()) + Math.random().toString(36).slice(2, 6)};
    const id = addHousingIdRef.current.id;
    const rec = {
      id,
      batch_id: batch.id,
      housing_name: name,
      status: 'active',
      current_count: null,
      current_count_date: null,
      start_date: addHousingStart || null,
      retired_date: null,
      notes: null,
    };
    // 242: insert-only; the server logs record.created on the housing.
    const outcome = await applyLayerOp({
      operation: 'housing.create',
      target: {housing_id: id},
      change: {batch_id: batch.id, housing_name: name, start_date: rec.start_date},
    });
    setAddHousingBusy(false);
    if (outcome.status !== 'saved') {
      setAddHousingErr(
        outcome.status === 'refused'
          ? 'That housing already exists. Try again.'
          : outcome.status === 'rejected'
            ? 'Could not save: ' + layerRejectionMessage(outcome)
            : outcome.status === 'unknown'
              ? outcome.message
              : 'Could not save. Try again.',
      );
      return;
    }
    addHousingIdRef.current = null;
    const created = (outcome.rows?.housing as HousingRow | undefined) || rec;
    if (typeof setLayerHousings === 'function') {
      const nextHousings = [...(layerHousings || []), created];
      setLayerHousings(nextHousings);
    }
    setShowAddHousing(false);
    setAddHousingName('');
    setAddHousingStart(todayStr());
    navigate('/layer/housings/' + id);
  }

  if (loading) {
    return <RecordPageLoading Header={Header} />;
  }

  if (loadError) {
    return (
      <RecordPageLoadError
        backLabel="Back to Layer Batches"
        onBack={() => navigate('/layer/batches')}
        notice={loadError}
        onRetry={loadAll}
        maxWidth={960}
        data-layer-batch-load-error="true"
      />
    );
  }

  if (!batch) {
    return (
      <RecordPageNotFound
        Header={Header}
        backLabel="Back to Layer Batches"
        onBack={() => navigate('/layer/batches')}
        message="Batch not found."
      />
    );
  }

  const batchHousings = (layerHousings || []).filter((h) => h.batch_id === batch.id);
  // IDENTITY COMES FROM THE SERVER ROW, NOT FROM `batch`.
  //
  // `batch` is resolved out of `batchOverlay ?? serverBatches`, and the edit
  // form autosaves. So keying on `batch.name` meant typing over the name
  // published a new overlay, flipped this false mid-session, and handed the
  // operator the generic lifecycle Status selector on the one row whose
  // non-active status is load-bearing for feed-cost fan-out. The server row is
  // the stable identity: the overlay cannot move it, and the name field below
  // is read-only for this batch anyway, so it cannot drift on the server side
  // either.
  const serverBatch = ((serverBatches || []) as BatchRow[]).find((b) => b.id === batchId) || null;
  const isRetHome = (serverBatch ? serverBatch.name : '') === 'Retirement Home';
  // The Retirement Home record IS a group, so its title carries the 10px dot.
  const titleDot = isRetHome ? layerGroupColor(groupPalette, 'Retirement Home') : null;
  const displayedNotes = isRetHome ? retirementHomeNotes(batch.notes, retirementHousing?.notes) : batch.notes;

  // Stats for this batch (compute locally so the page is self-contained).
  const allBatchStats = computeBatchStats([batch], batchHousings, rawLayerDailys, rawEggDailys);
  const allHousingStats = computeHousingStats([batch], batchHousings, rawLayerDailys, rawEggDailys);
  let s;
  if (isRetHome) {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - retHomePeriod);
    const cutoffStr = cutoff.toISOString().split('T')[0];
    const windowReports = rawLayerDailys.filter((d) => d.batch_id === batch.id && (d.date || '') >= cutoffStr);
    const anchor = batch.brooder_entry_date || batch.arrival_date || null;
    let totalFeed = 0;
    let totalMort = 0;
    let starterFeed = 0;
    let growerFeed = 0;
    let layerFeed = 0;
    windowReports.forEach((d) => {
      const f = parseFloat(String(d.feed_lbs)) || 0;
      totalFeed += f;
      totalMort += parseInt(String(d.mortality_count)) || 0;
      if (!anchor) layerFeed += f;
      else {
        try {
          // .getTime() is the ToPrimitive the `-` operator already performed on
          // both Dates; the subtraction and its result are unchanged.
          const days = Math.floor(
            (new Date(d.date + 'T12:00:00').getTime() - new Date(anchor + 'T12:00:00').getTime()) / 86400000,
          );
          if (days < 21) starterFeed += f;
          else if (days < 140) growerFeed += f;
          else layerFeed += f;
        } catch (_e) {
          layerFeed += f;
        }
      }
    });
    s = {
      totalFeed,
      totalMort,
      starterFeed,
      growerFeed,
      layerFeed,
      totalEggs: (allBatchStats[batch.id] || {}).totalEggs || 0,
    };
  } else {
    s = allBatchStats[batch.id] || {};
  }

  const feedCost = computeLayerFeedCost(s.starterFeed, s.growerFeed, s.layerFeed, batch);
  const totalDozens = s.totalEggs > 0 ? s.totalEggs / 12 : 0;
  const costPerDoz = feedCost != null && totalDozens > 0 ? feedCost / totalDozens : null;
  const fmt$ = (v: number) => '$' + v.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2});
  const periodLabel = isRetHome
    ? {30: 'Last 30 Days', 90: 'Last 90 Days', 180: 'Last 6 Months'}[retHomePeriod]
    : 'Lifetime';

  // Performance summary tiles (non-RetHome)
  let perfTiles = null;
  if (!isRetHome) {
    const orig = parseInt(String(batch.original_count)) || 0;
    const currentHens = batchHousings.reduce(
      (sum, h) => sum + computeHousingDisplayCount(h, rawLayerDailys, layerHousings),
      0,
    );
    const todayISOstr = new Date().toISOString().split('T')[0];
    let endDate = todayISOstr;
    if (batch.status === 'retired') {
      const ret = batchHousings
        .map((h) => h.retired_date)
        // `(d): d is string` annotates the SAME truthiness test .filter(Boolean)
        // ran, so the surviving dates are identical.
        .filter((d): d is string => Boolean(d))
        .sort();
      endDate = ret[ret.length - 1] || batch.schooner_exit_date || todayISOstr;
    }
    const anchor = batch.brooder_entry_date || batch.arrival_date || null;
    const batchAgeDays = anchor
      ? Math.max(
          0,
          Math.round((new Date(endDate + 'T12:00:00').getTime() - new Date(anchor + 'T12:00:00').getTime()) / 86400000),
        )
      : 0;
    const batchAgeMonths = +(batchAgeDays / 30.44).toFixed(1);
    const batchAgeStr = batchAgeDays > 0 ? `${batchAgeMonths} months (${batchAgeDays} days)` : '—';
    const firstHousingStart =
      batchHousings.length > 0
        ? batchHousings
            .map((h) => h.start_date)
            .filter((d): d is string => Boolean(d))
            .sort()[0]
        : null;
    const daysInHousing = firstHousingStart
      ? Math.max(
          0,
          Math.round(
            (new Date(endDate + 'T12:00:00').getTime() - new Date(firstHousingStart + 'T12:00:00').getTime()) /
              86400000,
          ),
        )
      : 0;
    const eggsPerHen = orig > 0 ? s.totalEggs / orig : null;
    const eggsPerHenPerDay = currentHens > 0 && daysInHousing > 0 ? s.totalEggs / (currentHens * daysInHousing) : null;
    const feedPerHen = orig > 0 ? s.totalFeed / orig : null;
    const costPerHen = feedCost != null && orig > 0 ? feedCost / orig : null;
    perfTiles = [
      {l: 'Batch Age', v: batchAgeStr, c: 'var(--text-primary)'},
      {l: 'Days in Housing', v: daysInHousing > 0 ? daysInHousing + ' days' : '—', c: 'var(--text-primary)'},
      {
        l: 'Original → Current',
        v: orig > 0 ? orig.toLocaleString() + ' → ' + currentHens.toLocaleString() : '—',
        c: 'var(--text-primary)',
      },
      {
        l: 'Dozens / Hen (lifetime)',
        v: eggsPerHen != null ? (eggsPerHen / 12).toFixed(1) + ' doz' : '—',
        c: 'var(--text-primary)',
      },
      {
        l: 'Eggs / Hen / Day (housing)',
        v: eggsPerHenPerDay != null ? eggsPerHenPerDay.toFixed(3) : '—',
        // Production-rate signal: at/above target reads ok, below reads warn.
        c:
          eggsPerHenPerDay == null
            ? 'var(--text-primary)'
            : eggsPerHenPerDay >= 0.7
              ? 'var(--ok-ink)'
              : 'var(--warn-ink)',
      },
      {
        l: 'Feed / Hen (lifetime)',
        v: feedPerHen != null ? feedPerHen.toFixed(1) + ' lbs' : '—',
        c: 'var(--text-primary)',
      },
      {l: 'Cost / Hen (lifetime)', v: costPerHen != null ? fmt$(costPerHen) : '—', c: 'var(--text-primary)'},
    ];
  }

  // ---- R25 per-card editors ----------------------------------------------
  // The former Edit modal's fields, unchanged and in the same order, split by
  // the card they belong to. All three edit the one shared draft through the
  // same confirmed autosave; only the active card renders its inputs.
  const editorFieldsetS: React.CSSProperties = {border: 0, margin: 0, padding: 0, minWidth: 0};
  // data-layer-batch-form-modal is the editor's existing hook (R34 keeps data-*
  // hooks); data-layer-batch-editor names which card is open.
  const editorAttrs = (card: BatchEditorCard) => ({
    'data-layer-batch-form-modal': '1',
    'data-layer-batch-editor': card,
  });
  const cardHead = (card: BatchEditorCard, title: string) => (
    <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12}}>
      <div style={cardTitleS}>{title}</div>
      {canEdit && editingCard !== card && (
        <button
          type="button"
          className="wcf-clickable"
          style={cardEditActionS}
          disabled={batchActionBusy}
          data-layer-batch-edit={batch.id}
          data-layer-batch-edit-card={card}
          onClick={() => void editCard(card)}
        >
          Edit
        </button>
      )}
    </div>
  );
  const detailsEditor = (
    <>
      {/* READ-ONLY FOR THE PSEUDO-BATCH. Its name is the key several
          load-bearing fences match on — feed-cost fan-out, the webform
          mirror, the broiler timeline — so renaming it here would
          silently un-fence it everywhere at once. */}

      <div className={recordFieldRowClass}>
        <span style={recordFieldLabel}>{isRetHome ? 'Name' : 'Batch Name *'}</span>
        <input
          value={bForm.name}
          onChange={(e) => updBatch((f) => ({...f, name: e.target.value}))}
          readOnly={isRetHome}
          disabled={isRetHome}
          data-layer-batch-name-locked={isRetHome ? '1' : undefined}
          style={recordControl}
        />
      </div>

      {/* Retirement Home edits the canonical Layer Group status. Its
          historical batch lifecycle remains unchanged for accounting. */}

      {isRetHome ? (
        <div className={recordFieldRowClass} data-layer-batch-lifecycle-locked="1">
          <label htmlFor="retirement-home-status" style={recordFieldLabel}>
            Status
          </label>
          <div>
            <select
              id="retirement-home-status"
              value={retirementHomeStatus ?? ''}
              onChange={(e) => void saveRetirementHomeStatus(e.target.value as LayerGroupStatus)}
              disabled={groupSaving || batchSaving}
              style={recordControl}
            >
              {!retirementHomeStatus && (
                <option value="" disabled>
                  Choose status
                </option>
              )}
              {LAYER_GROUP_STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <div style={{fontSize: 12, color: 'var(--ink-muted)', marginTop: 6}}>
              Inactive removes this group from new daily reports and missed-report reminders.
            </div>
            {groupError && (
              <div role="alert" style={{color: 'var(--danger-ink)', fontSize: 12}}>
                {groupError}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className={recordFieldRowClass}>
          <span style={recordFieldLabel}>Status</span>
          <select
            value={bForm.status}
            onChange={(e) => updBatch((f) => ({...f, status: e.target.value}))}
            style={recordControl}
          >
            <option value="active">Active</option>
            <option value="retired">Retired</option>
          </select>
        </div>
      )}

      {isRetHome && (
        <>
          <div className={recordFieldRowClass}>
            <label htmlFor="retirement-home-count" style={recordFieldLabel}>
              Current Count
            </label>
            <div>
              <input
                id="retirement-home-count"
                type="number"
                min="0"
                step="1"
                value={bForm.current_count}
                onChange={(e) => updBatch((form) => ({...form, current_count: e.target.value}))}
                disabled={batchSaving || groupSaving}
                style={recordControl}
              />
              {retirementHousing?.current_count_date && (
                <div style={{fontSize: 10, color: 'var(--ink-faint)', marginTop: 4}}>
                  Last set: {fmt(retirementHousing.current_count_date)}
                </div>
              )}
            </div>
          </div>
          <div className={recordFieldRowClass}>
            <label htmlFor="retirement-home-start" style={recordFieldLabel}>
              Start Date
            </label>
            <input
              id="retirement-home-start"
              type="date"
              value={bForm.start_date}
              onChange={(e) => updBatch((form) => ({...form, start_date: e.target.value}))}
              disabled={batchSaving || groupSaving}
              style={recordControl}
            />
          </div>
        </>
      )}

      {bForm.name !== 'Retirement Home' && (
        <div className={recordFieldRowClass}>
          <span style={recordFieldLabel}>Original Count</span>
          <input
            type="number"
            min="0"
            value={bForm.original_count || ''}
            onChange={(e) => updBatch((f) => ({...f, original_count: e.target.value}))}
            style={recordControl}
          />
        </div>
      )}

      {bForm.name !== 'Retirement Home' && (
        <div className={recordFieldRowClass}>
          <span style={recordFieldLabel}>Supplier</span>
          <input
            value={bForm.supplier}
            onChange={(e) => updBatch((f) => ({...f, supplier: e.target.value}))}
            style={recordControl}
          />
        </div>
      )}

      {bForm.name !== 'Retirement Home' && (
        <div className={recordFieldRowClass}>
          <span style={recordFieldLabel}>Cost per Bird ($)</span>
          <input
            type="number"
            min="0"
            step="0.01"
            style={{...recordControl, maxWidth: 160}}
            value={bForm.cost_per_bird || ''}
            onChange={(e) => updBatch((f) => ({...f, cost_per_bird: e.target.value}))}
          />
        </div>
      )}
    </>
  );
  const feedCostRates = (
    <>
      {/* Feed cost rates (read-only) */}

      {!isRetHome && (
        <div style={{borderTop: '1px solid var(--border)', paddingTop: 10, marginTop: 4}}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: 'var(--ink-muted)',
              letterSpacing: 0.5,
              marginBottom: 6,
            }}
          >
            Feed Cost Rates{' '}
            <span style={{fontWeight: 400, color: 'var(--ink-faint)'}}>{'(locked — set in Admin › Feed Costs)'}</span>
          </div>
          <div
            style={{
              display: 'flex',
              gap: 16,
              fontSize: 12,
              color: 'var(--ink)',
              padding: '8px 12px',
              background: 'var(--surface-2)',
              borderRadius: 10,
              border: '1px solid var(--border)',
            }}
          >
            <span>
              Starter:{' '}
              <strong>
                {batch.per_lb_starter_cost != null
                  ? '$' + parseFloat(String(batch.per_lb_starter_cost)).toFixed(3) + '/lb'
                  : '—'}
              </strong>
            </span>
            <span>
              Grower:{' '}
              <strong>
                {batch.per_lb_grower_cost != null
                  ? '$' + parseFloat(String(batch.per_lb_grower_cost)).toFixed(3) + '/lb'
                  : '—'}
              </strong>
            </span>
            <span>
              Layer:{' '}
              <strong>
                {batch.per_lb_layer_cost != null
                  ? '$' + parseFloat(String(batch.per_lb_layer_cost)).toFixed(3) + '/lb'
                  : '—'}
              </strong>
            </span>
          </div>
        </div>
      )}
    </>
  );
  const lifecycleEditor = (
    <>
      {/* Brooder phase */}

      {bForm.name !== 'Retirement Home' && (
        <div
          style={{
            borderTop: '1px solid var(--border)',
            paddingTop: 10,
            marginTop: 4,
            fontSize: 11,
            fontWeight: 700,
            color: 'var(--ink-muted)',
            letterSpacing: 0.5,
          }}
        >
          Brooder Phase <span style={{fontWeight: 400, color: 'var(--ink-faint)'}}>(fixed 3 weeks)</span>
        </div>
      )}

      {bForm.name !== 'Retirement Home' && (
        <div className={recordFieldRowClass}>
          <span style={recordFieldLabel}>Brooder</span>
          <select
            value={bForm.brooder_name}
            style={recordControl}
            onChange={(e) => {
              const val = e.target.value;
              const entry = bForm.brooder_entry_date;
              const exit = entry ? toISO(addDays(new Date(entry + 'T12:00:00'), 21)) : bForm.brooder_exit_date;
              const schoonerIn = exit || bForm.schooner_entry_date;
              const schoonerOut = schoonerIn
                ? toISO(addDays(new Date(schoonerIn + 'T12:00:00'), 119))
                : bForm.schooner_exit_date;
              updBatch((f) => ({
                ...f,
                brooder_name: val,
                brooder_exit_date: exit || f.brooder_exit_date,
                schooner_entry_date: schoonerIn || f.schooner_entry_date,
                schooner_exit_date: schoonerOut || f.schooner_exit_date,
              }));
            }}
          >
            <option value="">Select brooder…</option>
            {BROODERS.map((b) => {
              const conflict = layerAllocationConflicts(
                {...bForm, brooder_name: 'Brooder ' + b},
                batch.id,
                conflictBatches,
                layerBatches,
              ).some((warning) => warning.startsWith('Brooder '));
              return (
                <option key={b} value={'Brooder ' + b} style={{color: conflict ? 'var(--label)' : 'inherit'}}>
                  {'Brooder ' + b + (conflict ? ' ⚠ In use' : '')}
                </option>
              );
            })}
          </select>
        </div>
      )}

      {bForm.name !== 'Retirement Home' && (
        <div className={recordFieldRowClass}>
          <span style={recordFieldLabel}>Date in Brooder</span>
          <input
            type="date"
            style={recordControl}
            value={bForm.brooder_entry_date}
            onChange={(e) => {
              const entry = e.target.value;
              const exit = entry ? toISO(addDays(new Date(entry + 'T12:00:00'), 21)) : '';
              const schoonerOut = exit ? toISO(addDays(new Date(exit + 'T12:00:00'), 119)) : '';
              updBatch((f) => ({
                ...f,
                brooder_entry_date: entry,
                brooder_exit_date: exit,
                schooner_entry_date: exit,
                schooner_exit_date: schoonerOut,
                arrival_date: entry || f.arrival_date,
              }));
            }}
          />
        </div>
      )}

      {bForm.name !== 'Retirement Home' && (
        <div className={recordFieldRowClass}>
          <span style={recordFieldLabel}>
            Exit Date <span style={{color: 'var(--ink-faint)', fontWeight: 400}}>(auto)</span>
          </span>
          <input
            type="date"
            value={bForm.brooder_exit_date}
            readOnly
            style={{...recordControl, background: 'var(--surface-2)', color: 'var(--ink-muted)'}}
          />
        </div>
      )}

      {/* Schooner phase */}

      {bForm.name !== 'Retirement Home' && (
        <div
          style={{
            borderTop: '1px solid var(--border)',
            paddingTop: 10,
            marginTop: 4,
            fontSize: 11,
            fontWeight: 700,
            color: 'var(--ink-muted)',
            letterSpacing: 0.5,
          }}
        >
          Schooner Phase <span style={{fontWeight: 400, color: 'var(--ink-faint)'}}>(3 to 24 weeks)</span>
        </div>
      )}

      {bForm.name !== 'Retirement Home' && (
        <div className={recordFieldRowClass}>
          <span style={recordFieldLabel}>Schooner</span>
          <select
            value={bForm.schooner_name}
            onChange={(e) => updBatch((f) => ({...f, schooner_name: e.target.value}))}
            style={recordControl}
          >
            <option value="">Select schooner…</option>
            {SCHOONERS.map((sc) => {
              const conflict = layerAllocationConflicts(
                {...bForm, schooner_name: 'Schooner ' + sc},
                batch.id,
                conflictBatches,
                layerBatches,
              ).some((warning) => warning.startsWith('Schooner '));
              return (
                <option key={sc} value={'Schooner ' + sc} style={{color: conflict ? 'var(--label)' : 'inherit'}}>
                  {'Schooner ' + sc + (conflict ? ' ⚠ In use' : '')}
                </option>
              );
            })}
          </select>
        </div>
      )}

      {bForm.name !== 'Retirement Home' && (
        <div className={recordFieldRowClass}>
          <span style={recordFieldLabel}>
            Entry Date <span style={{color: 'var(--ink-faint)', fontWeight: 400}}>(auto)</span>
          </span>
          <input
            type="date"
            style={recordControl}
            value={bForm.schooner_entry_date}
            onChange={(e) => {
              const entry = e.target.value;
              const exit = entry ? toISO(addDays(new Date(entry + 'T12:00:00'), 119)) : '';
              updBatch((f) => ({...f, schooner_entry_date: entry, schooner_exit_date: exit}));
            }}
          />
        </div>
      )}

      {bForm.name !== 'Retirement Home' && (
        <div className={recordFieldRowClass}>
          <span style={recordFieldLabel}>
            Exit Date <span style={{color: 'var(--ink-faint)', fontWeight: 400}}>(editable)</span>
          </span>
          <input
            type="date"
            style={recordControl}
            value={bForm.schooner_exit_date}
            onChange={(e) => updBatch((f) => ({...f, schooner_exit_date: e.target.value}))}
          />
        </div>
      )}

      {bForm.name !== 'Retirement Home' &&
        bForm.schooner_entry_date &&
        bForm.schooner_exit_date &&
        (() => {
          // .getTime() is the ToPrimitive the `-` operator already performed
          // on both Dates; the subtraction and its result are unchanged.
          const weeks = Math.round(
            (new Date(bForm.schooner_exit_date + 'T12:00:00').getTime() -
              new Date(bForm.schooner_entry_date + 'T12:00:00').getTime()) /
              604800000,
          );
          const warn = weeks < 3 || weeks > 24;
          return (
            <div
              style={{
                fontSize: 11,
                padding: '4px 8px',
                borderRadius: 10,
                background: warn ? 'var(--warn-soft)' : 'var(--ok-soft)',
                color: warn ? 'var(--warn-ink)' : 'var(--ok-ink)',
                fontWeight: 600,
              }}
            >
              {warn ? '⚠ ' : ''}
              {weeks} weeks in schooner {warn ? '(expected 3 to 24 weeks)' : '✓'}
            </div>
          );
        })()}
    </>
  );
  const notesEditor = (
    <>
      <div>
        <div className={recordFieldRowClass} style={{borderBottom: 'none'}}>
          <span style={recordFieldLabel}>Notes</span>
          <textarea
            value={bForm.notes}
            onChange={(e) => updBatch((f) => ({...f, notes: e.target.value}))}
            rows={2}
            disabled={isRetHome && (batchSaving || groupSaving)}
            style={recordTextarea}
          />
        </div>
      </div>
    </>
  );
  // Save state, conflicts and the allocation confirm render inside whichever
  // card is open, exactly as they rendered inside the modal.
  const editorNotices = (
    <>
      {err && <div style={{color: 'var(--danger-ink)', fontSize: 12, fontWeight: 600}}>{err}</div>}
      <PigConflictNotice conflict={batchConflict} />
      {allocationWarning.length > 0 && (
        <InlineNotice
          notice={{kind: 'warning', message: allocationWarning.join('\n')}}
          action={
            <Button
              variant="secondary"
              disabled={batchSaving}
              onClick={() => {
                confirmedAllocation.current = layerAllocationKey(bFormRef.current);
                void flushBatchAutosave();
              }}
            >
              Save Anyway
            </Button>
          }
        />
      )}
      {batchSaveState === 'failed' && (
        <InlineNotice
          notice={{kind: 'error', message: 'Changes have not been fully saved. Your draft is still here.'}}
          action={
            <Button variant="secondary" onClick={() => void flushBatchAutosave()}>
              Retry Save
            </Button>
          }
        />
      )}
    </>
  );
  const retirementCount = retirementHousing?.current_count;

  return (
    <RecordPageFrame Header={Header}>
      <RecordPageBody maxWidth={960} data-layer-batch-record-loaded="true">
        <RecordBackLink label="Back to Layer Batches" onBack={() => navigate('/layer/batches')} />

        <RecordSequenceNav seq={recordSeq} currentId={batchId} onNavigate={navigateSeq} />

        <div style={{display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12}}>
          <RecordTitle margin={0}>
            {titleDot ? (
              <span style={{display: 'inline-flex', alignItems: 'center', gap: 8}}>
                <GroupDot color={titleDot} size={10} />
                {batch.name}
              </span>
            ) : (
              batch.name
            )}
          </RecordTitle>
          <Badge status={isRetHome ? retirementHomeStatus : batch.status}>
            {isRetHome
              ? retirementHomeStatus
                ? layerGroupStatusLabel(retirementHomeStatus)
                : 'Status not set'
              : batch.status}
          </Badge>
          {perfTiles && (
            <span className="poultry-meta">
              {perfTiles[0].v} · {perfTiles[1].v} in housing
            </span>
          )}
          {/* R25: the header's one action while a card is open. Done is the
              former modal's close: flush, and close only once it confirmed. */}
          {editingCard !== null && (
            <Button
              variant="primary"
              type="button"
              onClick={() => void closeBatchForm()}
              disabled={batchActionBusy || groupSaving || (isRetHome && batchSaving)}
              data-layer-batch-done={batch.id}
              style={{marginLeft: 'auto'}}
            >
              Done
            </Button>
          )}
        </div>

        {notice && <InlineNotice notice={notice} onDismiss={() => setNotice(null)} />}

        {isRetHome && (
          <div style={{display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 16, fontSize: 13}}>
            <span>
              Live hens:{' '}
              <strong>
                {retirementHousing
                  ? (computeProjectedCount(retirementHousing, rawLayerDailys, layerHousings)?.projected ?? '—')
                  : '—'}
              </strong>
            </span>
          </div>
        )}

        {/* Stats grid */}
        {isRetHome && (
          <Segmented
            aria-label="Report Period"
            value={String(retHomePeriod)}
            onChange={(value) => setRetHomePeriod(Number(value))}
            options={[
              {value: '30', label: '30 Days'},
              {value: '90', label: '90 Days'},
              {value: '180', label: '6 Months'},
            ]}
            style={{marginBottom: 12}}
          />
        )}

        <div className="poultry-record-metrics">
          {(isRetHome
            ? [
                {
                  label: `Total Feed (${periodLabel})`,
                  value: s.totalFeed > 0 ? Math.round(s.totalFeed).toLocaleString() + ' lb' : '—',
                },
                {label: 'Layer Feed', value: s.layerFeed > 0 ? Math.round(s.layerFeed).toLocaleString() + ' lb' : '—'},
                {
                  label: `Mortality (${periodLabel})`,
                  value: s.totalMort || '0',
                  color: s.totalMort > 10 ? 'var(--danger-ink)' : undefined,
                },
              ]
            : [
                {label: 'Original → Current Hens', value: perfTiles?.[2].v || '—'},
                {
                  label: 'Mortality',
                  value: s.totalMort || '0',
                  color: s.totalMort > 10 ? 'var(--danger-ink)' : undefined,
                },
                {
                  label: 'Total Feed',
                  value: s.totalFeed > 0 ? Math.round(s.totalFeed).toLocaleString() + ' lb' : '—',
                  hint: `${Math.round(s.starterFeed).toLocaleString()} starter · ${Math.round(s.growerFeed).toLocaleString()} grower · ${Math.round(s.layerFeed).toLocaleString()} layer`,
                  hintColor: s.starterFeed >= 1400 ? 'var(--danger-ink)' : undefined,
                },
                {label: 'Total Dozens', value: s.totalEggs > 0 ? Math.floor(s.totalEggs / 12).toLocaleString() : '—'},
                {label: 'Eggs / Hen / Day', value: perfTiles?.[4].v || '—', color: perfTiles?.[4].c},
                {
                  label: 'Cost / Dozen',
                  value: costPerDoz != null ? fmt$(costPerDoz) : '—',
                  hint: `Feed Cost ${feedCost != null ? fmt$(feedCost) : '—'}`,
                },
              ]
          ).map((metric) => (
            <div className="poultry-record-metric" key={metric.label}>
              <div className="poultry-meta">{metric.label}</div>
              <strong style={{color: metric.color}}>{metric.value}</strong>
              {'hint' in metric && (
                <div className="poultry-meta" style={{color: 'hintColor' in metric ? metric.hintColor : undefined}}>
                  {metric.hint}
                </div>
              )}
            </div>
          ))}
        </div>

        {perfTiles && (
          <section style={{marginBottom: 16}}>
            <h2 style={{...sectionTitleS, marginBottom: 8}}>Performance Summary</h2>
            <dl className="poultry-fact-rows">
              {perfTiles
                .filter((_, index) => [3, 5, 6].includes(index))
                .map((metric) => (
                  <div key={metric.l}>
                    <dt>{metric.l}</dt>
                    <dd>{metric.v}</dd>
                  </div>
                ))}
            </dl>
          </section>
        )}

        {/* R25: details, read first; Edit flips this card to inputs in place. */}
        <div style={{...recordCardS, padding: '16px 20px', marginBottom: 16}} data-layer-batch-card="details">
          {cardHead('details', isRetHome ? 'Retirement Home Details' : 'Batch Details')}
          {editingCard === 'details' ? (
            <fieldset disabled={batchActionBusy} style={editorFieldsetS} {...editorAttrs('details')}>
              {detailsEditor}
              {editorNotices}
            </fieldset>
          ) : isRetHome ? (
            <dl className="poultry-fact-rows">
              <div>
                <dt>Current Count</dt>
                <dd>
                  {retirementCount != null ? retirementCount : '—'}
                  {retirementHousing?.current_count_date ? ' · set ' + fmt(retirementHousing.current_count_date) : ''}
                </dd>
              </div>
              <div>
                <dt>Start Date</dt>
                <dd>{retirementHousing?.start_date ? fmt(retirementHousing.start_date) : '—'}</dd>
              </div>
            </dl>
          ) : (
            <dl className="poultry-fact-rows">
              <div>
                <dt>Original Count</dt>
                <dd>{batch.original_count != null ? batch.original_count : '—'}</dd>
              </div>
              <div>
                <dt>Supplier</dt>
                <dd>{batch.supplier || '—'}</dd>
              </div>
              <div>
                <dt>Cost per Bird</dt>
                <dd>{batch.cost_per_bird != null ? fmt$(Number(batch.cost_per_bird)) : '—'}</dd>
              </div>
            </dl>
          )}
          {feedCostRates}
        </div>

        {/* Lifecycle phases */}
        {!isRetHome && (
          <div style={{...recordCardS, padding: '16px 20px', marginBottom: 16}} data-layer-batch-card="lifecycle">
            {cardHead('lifecycle', 'Lifecycle Phases')}
            {editingCard === 'lifecycle' ? (
              <fieldset disabled={batchActionBusy} style={editorFieldsetS} {...editorAttrs('lifecycle')}>
                {lifecycleEditor}
                {editorNotices}
              </fieldset>
            ) : (
              <div className="poultry-phase-strip">
                {[
                  {
                    label: 'Brooder',
                    name: batch.brooder_name,
                    entry: batch.brooder_entry_date,
                    exit: batch.brooder_exit_date,
                  },
                  {
                    label: 'Schooner',
                    name: batch.schooner_name,
                    entry: batch.schooner_entry_date,
                    exit: batch.schooner_exit_date,
                  },
                  {
                    label: 'Housing',
                    name:
                      batchHousings
                        .filter((housing) => housing.status === 'active')
                        .map((housing) => housing.housing_name)
                        .join(', ') || '—',
                    entry: batchHousings[0]?.start_date,
                    exit: null,
                  },
                ].map((phase) => (
                  <div className="poultry-phase" key={phase.label}>
                    <span
                      className="poultry-phase-dot"
                      aria-hidden="true"
                      data-current={Boolean(
                        phase.entry &&
                        phase.entry <= toISO(new Date()) &&
                        (!phase.exit || phase.exit > toISO(new Date())) &&
                        batch.status !== 'retired',
                      )}
                    />
                    <div>
                      <div className="poultry-phase-label">{phase.label}</div>
                      <div className="poultry-meta">{phase.name || 'Not Set'}</div>
                      <div className="poultry-meta">
                        {phase.entry ? fmt(phase.entry) : '—'}
                        {phase.exit ? ' → ' + fmt(phase.exit) : phase.entry ? ' → present' : ''}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Housings */}
        {!isRetHome && (
          <div style={{marginBottom: 16}}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10}}>
              <h2 style={sectionTitleS}>Housings</h2>
              {canEdit && batch.status === 'active' && (
                <Button
                  variant="primary"
                  type="button"
                  onClick={() => {
                    setAddHousingErr('');
                    setAddHousingName('');
                    setAddHousingStart(todayStr());
                    setShowAddHousing(true);
                  }}
                  data-layer-batch-add-housing={batch.id}
                >
                  + Add Housing
                </Button>
              )}
            </div>
            {batchHousings.length === 0 && (
              <div style={{color: 'var(--ink-faint)', fontSize: 13, padding: '1rem 0'}}>No housings yet.</div>
            )}
            <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
              {batchHousings.map((h) => {
                const hs = allHousingStats[h.id] || {};
                const cap = getHousingCap(h.housing_name);
                const displayCount = computeHousingDisplayCount(h, rawLayerDailys, layerHousings);
                const proj = computeProjectedCount(h, rawLayerDailys, layerHousings);
                // Headline farm count = mortality-adjusted projected hens. computeProjectedCount
                // already falls back to the latest positive daily count when current_count is a
                // stale/empty 0, so an active housing (e.g. Eggmobile 3) never surfaces a bare
                // "Physical: 0" as the meaningful live count. The fallback test uses JS coercion
                // (h.current_count > 0), keeping currentHens the sole display-count owner.
                const liveHens = proj ? proj.projected : null;
                // Number() around anchor/projected/current_count: computeProjectedCount
                // can return null for either, and `null > 0` / `null < x` are relational
                // comparisons that already coerced through ToNumber. Number(null) is 0
                // and Number(undefined) is NaN, so every test keeps its old result.
                const liveHensLow = !!(
                  proj &&
                  Number(proj.anchor) > 0 &&
                  Number(proj.projected) < Number(proj.anchor) * 0.9
                );
                const anchorFromFallback = !!(proj && Number(proj.anchor) > 0 && !(Number(h.current_count) > 0));
                const showAnchorCtx = !!(proj && proj.anchorDate && (Number(proj.mortSince) > 0 || anchorFromFallback));
                const util = displayCount && cap ? Math.round((displayCount / cap) * 100) : null;
                const housingDot = layerGroupColor(groupPalette, h.housing_name);
                return (
                  <div
                    key={h.id}
                    data-layer-housing-tile={h.id}
                    {...openableProps(() =>
                      navigate(
                        '/layer/housings/' + h.id,
                        recordSeqNavOptions(labeledSeqItems(batchHousings, 'housing_name')),
                      ),
                    )}
                    className="hoverable-tile"
                    style={{
                      ...recordCardS,
                      // R06: the housing's group tint.
                      background: layerGroupTint(housingDot) ?? recordCardS.background,
                      padding: '14px 18px',
                      display: 'flex',
                      gap: 16,
                      alignItems: 'center',
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{flex: 1}}>
                      <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap'}}>
                        {housingDot && <GroupDot color={housingDot} />}
                        <span style={{fontSize: 14, fontWeight: 800, color: 'var(--ink)'}}>{h.housing_name}</span>
                        <Badge variant={h.status === 'active' ? 'ok' : 'neutral'}>{h.status}</Badge>
                        {h.start_date && (
                          <span style={{fontSize: 11, color: 'var(--ink-muted)'}}>from {fmt(h.start_date)}</span>
                        )}
                        {h.retired_date && (
                          <span style={{fontSize: 11, color: 'var(--ink-faint)'}}>{'→ ' + fmt(h.retired_date)}</span>
                        )}
                      </div>
                      <div
                        style={{display: 'flex', gap: 16, fontSize: 11, color: 'var(--ink-muted)', flexWrap: 'wrap'}}
                      >
                        <span>
                          Live hens:{' '}
                          {liveHens == null ? (
                            <strong style={{color: 'var(--ink)'}}>—</strong>
                          ) : liveHensLow ? (
                            <StatusText tone="danger">{liveHens}</StatusText>
                          ) : (
                            <strong style={{color: 'var(--text-primary)'}}>{liveHens}</strong>
                          )}
                        </span>
                        {showAnchorCtx && (
                          <span
                            style={{color: 'var(--ink-muted)'}}
                            title={
                              'Physical anchor ' +
                              proj.anchor +
                              (proj.anchorDate ? ' on ' + fmt(proj.anchorDate) : '') +
                              (proj.mortSince > 0 ? ', minus ' + proj.mortSince + ' mortalities since' : '')
                            }
                          >
                            anchor <strong style={{color: 'var(--ink)'}}>{proj.anchor}</strong>
                            {proj.mortSince > 0 ? ' (−' + proj.mortSince + ')' : ''}
                            {proj.anchorDate ? ' · ' + fmt(proj.anchorDate) : ''}
                          </span>
                        )}
                        <span>
                          Capacity: <strong style={{color: 'var(--ink)'}}>{cap === 9999 ? 'Unlimited' : cap}</strong>
                        </span>
                        {util !== null && (
                          <span>
                            Utilization:{' '}
                            <StatusText tone={util > 95 ? 'danger' : util > 80 ? 'warn' : 'ok'}>
                              {util + '%'}
                            </StatusText>
                          </span>
                        )}
                        <span style={{fontSize: 11, color: 'var(--ink-muted)'}}>
                          Feed: {hs.totalFeed > 0 ? Math.round(hs.totalFeed) + ' lbs' : '—'} · Eggs:{' '}
                          {hs.totalEggs > 0 ? hs.totalEggs.toLocaleString() : '—'}
                        </span>
                      </div>
                    </div>
                    {/* R20: a click-through row carries one chevron. */}
                    <span className="chev" aria-hidden="true">
                      ›
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* R25: notes, read first. An empty note is the muted dash (R14). */}
        <div style={{...recordCardS, padding: '16px 20px', marginBottom: 16}} data-layer-batch-card="notes">
          {cardHead('notes', 'Notes')}
          {editingCard === 'notes' ? (
            <fieldset disabled={batchActionBusy} style={editorFieldsetS} {...editorAttrs('notes')}>
              {notesEditor}
              {editorNotices}
            </fieldset>
          ) : (
            <div style={{fontSize: 13, color: displayedNotes ? 'var(--ink)' : 'var(--label)', whiteSpace: 'pre-wrap'}}>
              {displayedNotes || '—'}
            </div>
          )}
        </div>

        <RecordCollaborationSection
          sb={sb}
          authState={authState}
          entityType="layer.batch"
          entityId={batch.id}
          entityLabel={batch.name as string}
        />
        {isRetHome && <RetirementHomeHistory sb={sb} authState={authState} housings={batchHousings} />}
        {/* R24: Delete is a danger text action at the foot, below Comments and
            Activity — the same role gate, handler and confirmation it had in
            the editor's footer. */}
        {!isRetHome && canEdit && (
          <Button
            variant="danger"
            type="button"
            onClick={handleDeleteBatch}
            disabled={batchActionBusy}
            data-layer-batch-delete={batch.id}
            style={{marginTop: 12}}
          >
            Delete Batch
          </Button>
        )}
      </RecordPageBody>

      {/* + Add Housing helper modal */}
      {!isRetHome && showAddHousing && (
        <Modal
          title={'Add Housing'}
          onClose={() => {
            if (!addHousingBusy) setShowAddHousing(false);
          }}
          size="sm"
          dialogAttrs={{'data-layer-add-housing-modal': '1'}}
          footer={
            <ModalFooter
              primaryLabel="Save & Open"
              onPrimary={saveNewHousing}
              busy={addHousingBusy}
              primaryDisabled={!addHousingName}
              cancelDisabled={addHousingBusy}
              primaryAttrs={{'data-layer-add-housing-save': '1'}}
            />
          }
        >
          <div>
            <div className={recordFieldRowClass}>
              <span style={recordFieldLabel}>Housing (Layer Group) *</span>
              <select value={addHousingName} onChange={(e) => setAddHousingName(e.target.value)} style={recordControl}>
                <option value="">{'Select housing…'}</option>
                {(layerGroups || []).map((g) => {
                  const owning = (layerHousings || []).find((h) => h.housing_name === g.name && h.status === 'active');
                  const locked = !!owning;
                  const owningBatch = owning ? (layerBatches || []).find((b) => b.id === owning.batch_id) : null;
                  const label = g.name + (locked ? ' ⚠ In use' + (owningBatch ? ' by ' + owningBatch.name : '') : '');
                  return (
                    <option key={g.id} value={g.name} disabled={locked}>
                      {label}
                    </option>
                  );
                })}
              </select>
            </div>

            <div className={recordFieldRowClass}>
              <span style={recordFieldLabel}>Start Date</span>
              <input
                type="date"
                value={addHousingStart}
                onChange={(e) => setAddHousingStart(e.target.value)}
                style={recordControl}
              />
            </div>

            <div
              style={{
                fontSize: 11,
                color: 'var(--ink-muted)',
                background: 'var(--surface-2)',
                border: '1px solid var(--border)',
                borderRadius: 10,
                padding: '8px 10px',
              }}
            >
              Saves a housing shell and opens its record page. Fill in count, status, and notes there.
            </div>

            {addHousingErr && (
              <div style={{color: 'var(--danger-ink)', fontSize: 12, fontWeight: 600}}>{addHousingErr}</div>
            )}
          </div>
        </Modal>
      )}
    </RecordPageFrame>
  );
}
